import { ArrowLeft } from "lucide-react";
import { useRef } from "react";
import type { PointerEvent } from "react";
import type { ViewId } from "./AppShell";

export interface SpotlightNavigationItem {
  id: ViewId;
  label: string;
  active: boolean;
  onClick: () => void;
}

export interface SpotlightSubNavigationItem {
  id: string;
  label: string;
  active: boolean;
  onClick: () => void;
}

interface SpotlightNavbarProps {
  activeView: ViewId;
  items: SpotlightNavigationItem[];
  subItems: SpotlightSubNavigationItem[];
  onNavigateLanding: () => void;
}

export function SpotlightNavbar({ activeView, items, subItems, onNavigateLanding }: SpotlightNavbarProps) {
  const navRef = useRef<HTMLElement>(null);
  const isLanding = activeView === "landing";

  const handlePointerMove = (event: PointerEvent<HTMLElement>) => {
    if (event.pointerType === "touch" || !navRef.current) return;
    const bounds = navRef.current.getBoundingClientRect();
    navRef.current.style.setProperty("--spotlight-x", `${event.clientX - bounds.left}px`);
  };

  return (
    <div className="spotlight-navigation">
      <nav
        aria-label="Workspace navigation"
        className="spotlight-navbar"
        onPointerLeave={() => navRef.current?.style.setProperty("--spotlight-x", "-200px")}
        onPointerMove={handlePointerMove}
        ref={navRef}
      >
        <div className="spotlight-navbar__items">
          <button
            aria-current={isLanding ? "page" : undefined}
            className={`spotlight-navbar__item${isLanding ? " is-active" : ""}`}
            onClick={onNavigateLanding}
            type="button"
          >
            <span>Home</span>
          </button>
          {items.map((item) => {
            return (
              <button
                aria-current={item.active ? "page" : undefined}
                className={`spotlight-navbar__item${item.active ? " is-active" : ""}`}
                key={item.id}
                onClick={item.onClick}
                type="button"
              >
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
      {!isLanding && subItems.length > 0 && (
        <nav className="spotlight-subnav" aria-label="Section navigation">
          <button className="spotlight-subnav__back" onClick={onNavigateLanding} title="All workspaces" type="button">
            <ArrowLeft size={14} aria-hidden="true" />
            <span>Workspaces</span>
          </button>
          <span className="spotlight-navbar__divider" aria-hidden="true" />
          {subItems.map((item) => (
            <button
              aria-current={item.active ? "page" : undefined}
              className={`spotlight-subnav__item${item.active ? " is-active" : ""}`}
              key={item.id}
              onClick={item.onClick}
              type="button"
            >
              {item.label}
            </button>
          ))}
        </nav>
      )}
    </div>
  );
}
